import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../api/client";
import {
  comboForCustomer,
  comboHistory,
  comboPioneerBranches,
  comboRecharge,
  comboRecheck,
} from "../../api/comboApi";
import { formatRupees } from "../../utils/providers";
import { pioneerRechargeOptions, stbPkgids } from "../../api/pioneerApi";
import AddonPicker from "../PioneerIptv/AddonPicker";
import { fmtDate, parseDate, selectedConflicts } from "../PioneerIptv/packRules";
import "../PioneerIptv/pioneer.css";
import { comboLabel, errMsg, fmtDay, fmtDayTime, newRequestKey } from "../Combo/comboUtils";
import "../Combo/combo.css";
import "./ComboRechargeTab.css";

const STATUS_TEXT = {
  success: "Recharged",
  failed: "Failed",
  pending: "Pending",
  skipped: "Skipped",
  partial: "Partly done",
  processing: "In progress",
};

const accountLabel = (a) => a.internet_username || a.username || `Account #${a.id}`;

const isAccountActive = (a) => String(a?.status || "").toLowerCase() === "active";
const accountOption = (a) => `${accountLabel(a)} — ${isAccountActive(a) ? "active" : (a.status || "not active").toLowerCase()}`;

const isStbActive = (stb) => !!stb?.expiration && new Date(stb.expiration) >= new Date();

/** One provider's line inside a combo recharge, with its Recheck button. */
const ItemRow = ({ item, busy, onRecheck }) => (
  <div className={`cmbr-item cmbr-item--${item.status}`}>
    <div className="cmbr-item__main">
      <div className="cmbr-item__title">
        <strong>{comboLabel(item.provider)}</strong>
        {item.is_free ? <em className="cmbr-free">FREE</em> : null}
        {item.is_advance ? <span className="cmb-badge cmb-badge--info">Advance</span> : null}
      </div>
      <div className="cmbr-item__plan">{item.plan_name || "—"}</div>
      {item.detail ? <div className="cmb-hint">{item.detail}</div> : null}

      {item.status === "success" ? (
        <div className="cmb-hint">
          {item.actual_expiry
            ? `Valid till ${fmtDay(item.actual_expiry)}`
            : item.expected_expiry
              ? `Valid till ${fmtDay(item.expected_expiry)}`
              : null}
          {item.resolved_by === "verified" ? " · confirmed from provider on recheck" : ""}
          {item.is_advance && !item.actual_expiry && item.expected_expiry
            ? " · starts after the current plan ends"
            : ""}
        </div>
      ) : null}

      {item.status !== "success" && item.error ? (
        <div className="cmb-error-text">{item.error}</div>
      ) : null}
      {item.status === "pending" && !item.error ? (
        <div className="cmb-hint">Not completed yet — use Recheck to finish it. No extra charge.</div>
      ) : null}
    </div>

    <div className="cmbr-item__side">
      <span className={`cmb-badge cmb-badge--${item.status}`}>{STATUS_TEXT[item.status] || item.status}</span>
      {item.can_recheck ? (
        <button
          type="button"
          className="cmb-btn cmb-btn--sm cmb-btn--primary"
          disabled={busy}
          onClick={() => onRecheck(item)}
        >
          {busy ? "Checking…" : "Recheck"}
        </button>
      ) : null}
    </div>
  </div>
);

const ComboRechargeTab = ({
  customerId,
  operatorId,
  internetAccounts = [],
  dvplAccounts = [],
  pioneerStbs = [],
  onChanged,
}) => {
  const [offers, setOffers] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Accounts / boxes running on a combo: [{ provider, key, combo_id, combo_name, till_text }]
  const [locks, setLocks] = useState([]);

  // Recharge dialog
  const [dlg, setDlg] = useState(null); // { combo, inputs, result }
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestKey = useRef(null);
  // IPTV add-ons for the combo's DPO: { opts, error } (pioneer_recharge_options.php)
  const [iptv, setIptv] = useState(null);

  // Recheck
  const [busyItem, setBusyItem] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null); // { item, message }

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [o, h, l] = await Promise.all([
        comboForCustomer(customerId),
        comboHistory(customerId),
        apiClient.get(`/customer/combo/combo_locks.php?user_id=${customerId}`).catch(() => ({ data: { locks: [] } })),
      ]);
      setLocks(l.data?.locks || []);
      setOffers(o.data.combos || []);
      setWallet(typeof o.data.wallet === "number" ? o.data.wallet : null);
      setHistory(h.data.history || h.data.combo_recharges || []);
    } catch (err) {
      setLoadError(errMsg(err, "Failed to load combos"));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // ── Open the dialog with sensible defaults ────────────────
  const openDialog = async (combo, advanceMode = false) => {
    const has = (p) => combo.items.some((i) => i.provider === p && i.status !== "skipped");
    const inputs = {};
    // Advance renewal of the combo the customer is on: the same account / box, fixed.
    const lockOf = (p) => locks.find((l) => l.provider === p && Number(l.combo_id) === Number(combo.id));

    // Prefer an account that has expired: a combo is recharged when something has run out.
    const pickAccount = (list) => {
      const expired = list.filter((a) => !isAccountActive(a));
      const pool = expired.length ? expired : list;
      return (pool.find((a) => Number(a.is_default) === 1) || pool[0] || {}).id || "";
    };

    if (has("internet")) {
      const l = advanceMode && lockOf("internet");
      inputs.internet = { account_id: l ? Number(l.key) : pickAccount(internetAccounts), fixed: !!l };
    }
    if (has("dvpl")) {
      const l = advanceMode && lockOf("dvpl");
      inputs.dvpl = { account_id: l ? Number(l.key) : pickAccount(dvplAccounts), fixed: !!l };
    }
    if (has("pioneeriptv")) {
      const l = advanceMode && lockOf("pioneeriptv");
      inputs.pioneeriptv = {
        device_id: l
          ? l.key
          : pioneerStbs.length === 1
            ? pioneerStbs[0].stb_box
            : (pioneerStbs.filter((x) => !isStbActive(x)).length === 1 ? pioneerStbs.find((x) => !isStbActive(x)).stb_box : ""),
        branch_id: "",
        advance: !!l,
        fixed: !!l,
        addon_ids: [],
      };
    }

    requestKey.current = newRequestKey();
    setDlg({ combo, inputs, result: null, advance: advanceMode });
    setIptv(null);

    if (inputs.pioneeriptv) {
      setBranchesLoading(true);
      try {
        const { data } = await comboPioneerBranches(operatorId);
        const list = data?.branches || [];
        setBranches(list);
        if (list.length === 1) {
          setDlg((d) => d && { ...d, inputs: { ...d.inputs, pioneeriptv: { ...d.inputs.pioneeriptv, branch_id: list[0].id } } });
        }
      } catch (err) {
        setBranches([]);
        toast.error("Failed to load Pioneer IPTV branches");
      } finally {
        setBranchesLoading(false);
      }
    }
  };

  const closeDialog = () => {
    if (submitting) return;
    setDlg(null);
  };

  const setInput = (provider, patch) =>
    setDlg((d) => d && { ...d, inputs: { ...d.inputs, [provider]: { ...d.inputs[provider], ...patch } } });

  // What still has to be chosen before the button unlocks.
  const missing = useMemo(() => {
    if (!dlg || dlg.result) return [];
    const out = [];
    const { inputs } = dlg;
    if (inputs.internet) {
      if (!internetAccounts.length) out.push("The customer has no linked Pioneer broadband account.");
      else if (!inputs.internet.account_id) out.push("Choose the broadband account.");
    }
    if (inputs.dvpl) {
      if (!dvplAccounts.length) out.push("The customer has no linked DVPL account.");
      else if (!inputs.dvpl.account_id) out.push("Choose the DVPL account.");
    }
    if (inputs.pioneeriptv) {
      if (!pioneerStbs.length) out.push("The customer has no Pioneer IPTV set-top box.");
      else if (!inputs.pioneeriptv.device_id) out.push("Choose the set-top box.");
      if (!branchesLoading && !branches.length) out.push("No Pioneer IPTV branch is assigned to this operator.");
      else if (!inputs.pioneeriptv.branch_id) out.push("Choose the Pioneer IPTV branch.");
    }
    return out;
  }, [dlg, internetAccounts, dvplAccounts, pioneerStbs, branches, branchesLoading]);

  // ── IPTV add-ons (loaded per box: an advance renewal keeps the box's current add-ons) ──
  const iptvKey = dlg && dlg.inputs.pioneeriptv && !dlg.result
    ? `${dlg.combo.id}|${dlg.inputs.pioneeriptv.advance ? dlg.inputs.pioneeriptv.device_id : ""}`
    : null;
  useEffect(() => {
    if (!iptvKey || !dlg) return undefined;
    let cancelled = false;
    const dpoItem = dlg.combo.items.find((i) => i.provider === "pioneeriptv");
    const adv = !!dlg.inputs.pioneeriptv.advance;
    const stb = pioneerStbs.find((x) => String(x.stb_box) === String(dlg.inputs.pioneeriptv.device_id));
    setIptv(null);
    pioneerRechargeOptions(customerId, {
      // combo items saved now point at the pack (sub_plan_id = Pioneer id); older ones at ott_plans
      dpoPackId: dpoItem && dpoItem.sub_plan_id ? dpoItem.plan_id : undefined,
      dpoPlanId: dpoItem && !dpoItem.sub_plan_id ? dpoItem.plan_id : undefined,
      currentPkgids: adv && stb ? stbPkgids(stb) : undefined,
    })
      .then(({ data }) => { if (!cancelled) setIptv({ opts: data, error: "" }); })
      .catch((err) => { if (!cancelled) setIptv({ opts: null, error: errMsg(err, "Could not load the IPTV add-ons") }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iptvKey]);

  // ── IPTV add-ons: charged x the combo's IPTV months, on top of the combo price ──
  const iptvMonths = Math.max(1, Number(dlg?.combo?.iptv_months || 1));
  const iptvBase = useMemo(
    () => (iptv?.opts ? [iptv.opts.fixed_dpo, iptv.opts.fta].filter(Boolean) : []),
    [iptv]
  );
  const iptvAddons = useMemo(
    () => (iptv?.opts ? [...(iptv.opts.addons["A-la-carte"] || []), ...(iptv.opts.addons.Broadcaster || [])] : []),
    [iptv]
  );
  const iptvAdvance = !!dlg?.inputs?.pioneeriptv?.advance;
  const chosenAddonIds = dlg?.inputs?.pioneeriptv?.addon_ids || [];
  const chosenAddons = iptvAdvance
    ? (iptv?.opts?.current?.addons || [])
    : chosenAddonIds.map((id) => iptvAddons.find((a) => a.id === id)).filter(Boolean);
  const addonConflicts = useMemo(
    () => (iptvAdvance ? new Map() : selectedConflicts(iptvBase, chosenAddons)),
    [iptvAdvance, iptvBase, chosenAddons]
  );
  const addonMonthly = chosenAddons.reduce((s, a) => s + Number(a.price || 0), 0);
  const addonTotal = Math.round(addonMonthly * iptvMonths * 100) / 100;
  const payTotal = dlg ? Math.round((Number(dlg.combo.price) + addonTotal) * 100) / 100 : 0;
  const stbPaidTill = dlg?.inputs?.pioneeriptv && iptv?.opts?.paid_till
    ? iptv.opts.paid_till[dlg.inputs.pioneeriptv.device_id]
    : null;
  // While the customer is on an active combo, only that combo is offered (advance renewal);
  // once it has expired, every combo is shown again.
  const activeComboIds = new Set(locks.map((l) => Number(l.combo_id)));
  const shownOffers = activeComboIds.size ? offers.filter((c) => activeComboIds.has(Number(c.id))) : offers;
  const hiddenOffers = offers.length - shownOffers.length;

  const allMissing = [...missing];
  // A combo is recharged only when the chosen account AND box have both expired / are not
  // active. Only an advance renewal of the combo they are already on is allowed while active.
  if (dlg && !dlg.result && !missing.length) {
    const { inputs } = dlg;
    const acc = (list, id) => list.find((a) => String(a.id) === String(id));
    const parts = [];
    if (inputs.internet) parts.push({ p: "internet", key: String(inputs.internet.account_id), what: "Internet account", active: isAccountActive(acc(internetAccounts, inputs.internet.account_id)) });
    if (inputs.dvpl) parts.push({ p: "dvpl", key: String(inputs.dvpl.account_id), what: "DVPL account", active: isAccountActive(acc(dvplAccounts, inputs.dvpl.account_id)) });
    if (inputs.pioneeriptv) parts.push({ p: "pioneeriptv", key: String(inputs.pioneeriptv.device_id), what: "set-top box", active: isStbActive(pioneerStbs.find((x) => String(x.stb_box) === String(inputs.pioneeriptv.device_id))) });
    const lockFor = (x) => locks.find((l) => l.provider === x.p && String(l.key) === x.key);
    const other = parts.map(lockFor).find((l) => l && Number(l.combo_id) !== Number(dlg.combo.id));
    if (other) {
      allMissing.push(`This customer is on the combo "${other.combo_name}" till ${other.till_text}. Renew that combo, or recharge another one after it ends.`);
    } else {
      const activeNotOnThis = parts.filter((x) => x.active && !lockFor(x));
      if (activeNotOnThis.length) {
        allMissing.push(`The ${activeNotOnThis.map((x) => x.what).join(" and the ")} ${activeNotOnThis.length > 1 ? "are" : "is"} still active. A combo can be recharged only when both the Internet account and the set-top box have expired or are not active.`);
      }
    }
  }
  if (dlg && !dlg.result && addonConflicts.size) allMissing.push("Remove the IPTV add-ons marked in red — their channels are already included.");
  if (dlg && !dlg.result && stbPaidTill) allMissing.push(`This set-top box is already paid till ${fmtDate(parseDate(stbPaidTill))}.`);

  const walletShort = dlg && wallet !== null && payTotal > wallet;

  const submit = async () => {
    if (!dlg || submitting || allMissing.length) return;
    setSubmitting(true);

    const components = {};
    Object.keys(dlg.inputs).forEach((p) => {
      const v = dlg.inputs[p];
      components[p] =
        p === "pioneeriptv"
          ? { device_id: String(v.device_id), branch_id: Number(v.branch_id), advance: !!v.advance, addon_ids: v.advance ? [] : v.addon_ids || [] }
          : { account_id: Number(v.account_id) };
    });

    try {
      const { data } = await comboRecharge({
        user_id: customerId,
        combo_id: dlg.combo.id,
        request_key: requestKey.current,
        components,
      });

      if (typeof data.wallet === "number") setWallet(data.wallet);
      setDlg((d) => d && { ...d, result: { recharge: data.combo_recharge, message: data.message } });

      const st = data.combo_recharge?.status;
      if (st === "success") toast.success("Combo recharged successfully");
      else toast.warn("Combo charged — some plans need a Recheck");

      load();
      if (onChanged) onChanged();
    } catch (err) {
      // A real HTTP error means nothing was charged (the server only returns
      // 2xx once the purchase is saved). If the network dropped instead, the
      // same request_key makes a retry safe: the server returns the first one.
      toast.error(errMsg(err, "Combo recharge failed"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Recheck ───────────────────────────────────────────────
  const doRecheck = async (item, confirmRetry = false) => {
    setBusyItem(item.id);
    try {
      const { data } = await comboRecheck(item.id, confirmRetry);

      // Keep an open result dialog in step with the new state.
      setDlg((d) => (d && d.result ? { ...d, result: { ...d.result, recharge: data.combo_recharge } } : d));

      if (data.result === "needs_confirmation") {
        setConfirmItem({ item, message: data.message });
      } else if (data.result === "failed" || data.result === "unavailable") {
        toast.error(data.message);
      } else {
        toast.success(data.message);
      }
      await load();
      if (onChanged && ["verified", "recharged"].includes(data.result)) onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Recheck failed"));
    } finally {
      setBusyItem(null);
    }
  };

  const onRecheck = (item) => doRecheck(item, false);

  // ── Render helpers ────────────────────────────────────────
  const renderInputs = () => {
    const { inputs } = dlg;
    return (
      <>
        {inputs.internet && (
          <div className="cmb-field">
            <label htmlFor="cmbr-inet">Pioneer broadband account</label>
            <select id="cmbr-inet" value={inputs.internet.account_id} disabled={!!inputs.internet.fixed}
              onChange={(e) => setInput("internet", { account_id: e.target.value })}>
              <option value="">Select account</option>
              {internetAccounts.map((a) => <option key={a.id} value={a.id}>{accountOption(a)}</option>)}
            </select>
          </div>
        )}

        {inputs.dvpl && (
          <div className="cmb-field">
            <label htmlFor="cmbr-dvpl">DVPL account</label>
            <select id="cmbr-dvpl" value={inputs.dvpl.account_id} disabled={!!inputs.dvpl.fixed}
              onChange={(e) => setInput("dvpl", { account_id: e.target.value })}>
              <option value="">Select account</option>
              {dvplAccounts.map((a) => <option key={a.id} value={a.id}>{accountOption(a)}</option>)}
            </select>
          </div>
        )}

        {inputs.pioneeriptv && (
          <>
            <div className="cmb-field">
              <label htmlFor="cmbr-stb">Pioneer IPTV set-top box</label>
              <select id="cmbr-stb" value={inputs.pioneeriptv.device_id} disabled={!!inputs.pioneeriptv.fixed}
                onChange={(e) => setInput("pioneeriptv", { device_id: e.target.value, advance: false })}>
                <option value="">Select box</option>
                {pioneerStbs.map((s) => (
                  <option key={s.stb_box} value={s.stb_box}>
                    {s.stb_box}{s.expiration ? ` — ${isStbActive(s) ? "active till" : "expired"} ${fmtDay(s.expiration)}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {branches.length > 1 && (
              <div className="cmb-field">
                <label htmlFor="cmbr-branch">Pioneer IPTV branch</label>
                <select id="cmbr-branch" value={inputs.pioneeriptv.branch_id}
                  onChange={(e) => setInput("pioneeriptv", { branch_id: e.target.value })}>
                  <option value="">Select branch</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.display_name}</option>)}
                </select>
              </div>
            )}
            {branches.length === 1 && (
              <div className="cmb-hint">Branch: <strong>{branches[0].display_name}</strong></div>
            )}

            {inputs.pioneeriptv.advance && (
              <div className="cmb-note cmb-note--info">
                Advance renewal: the same packs start after the box's current expiry. Packs can't be changed.
              </div>
            )}

            <div className="piptv-combo-iptv">
              <div className="piptv-combo-iptv__head">
                <strong>IPTV add-ons {iptvAdvance ? null : <span className="piptv-optional">(optional)</span>}</strong>
                <span className="cmb-hint">
                  IPTV runs {iptvMonths} {iptvMonths === 1 ? "month" : "months"} ({iptvMonths * 30} days) with this combo · FTA included
                </span>
              </div>
              {!iptv ? (
                <div className="cmb-hint">Loading add-ons…</div>
              ) : iptv.error ? (
                <div className="cmb-note cmb-note--warn">{iptv.error}</div>
              ) : iptvAdvance ? (
                <div className="cmb-hint">
                  Advance renewal keeps the box's current add-ons
                  {chosenAddons.length
                    ? `: ${chosenAddons.map((a) => `${a.name} ${formatRupees(a.price)}`).join(", ")}${iptvMonths > 1 ? ` (× ${iptvMonths} months)` : ""}, added to the combo price.`
                    : " (it has none)."}
                </div>
              ) : (
                <>
                  <div className="cmb-hint">
                    A-la-carte channels and broadcaster packs are added on top of the combo price
                    {iptvMonths > 1 ? ` (price × ${iptvMonths} months)` : ""}. Channels already in the DPO can't be added again.
                  </div>
                  <AddonPicker
                    addons={iptv.opts.addons}
                    selectedIds={chosenAddonIds}
                    onChange={(ids) => setInput("pioneeriptv", { addon_ids: ids })}
                    basePacks={iptvBase}
                    conflicts={addonConflicts}
                    months={iptvMonths}
                    disabled={submitting}
                  />
                </>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="cmbr">
      <div className="cmbr-head">
        <div>
          <h2 className="cmbr-title">COMBO RECHARGE</h2>
          <p className="cmb-hint">
            One payment, several plans. Each provider is recharged separately — if one fails, use
            Recheck on it; you are never charged twice.
          </p>
        </div>
        {wallet !== null && <div className="cmbr-wallet">Wallet <strong>{formatRupees(wallet)}</strong></div>}
      </div>

      {loading ? (
        <div className="cmb-empty">Loading combos…</div>
      ) : loadError ? (
        <div className="cmb-note cmb-note--danger">
          {loadError} <button type="button" className="cmb-btn cmb-btn--sm" onClick={load}>Try again</button>
        </div>
      ) : (
        <>
          {hiddenOffers > 0 && (
            <div className="cmb-note cmb-note--info" style={{ marginBottom: 12 }}>
              The customer is on a combo till {locks[0]?.till_text}. Only that combo can be renewed now;
              the other {hiddenOffers === 1 ? "combo shows" : `${hiddenOffers} combos show`} again after it expires.
            </div>
          )}
          {offers.length === 0 ? (
            <div className="cmb-empty">
              <strong>No combos available</strong>
              Ask your admin to assign combos to this operator account.
            </div>
          ) : (
            <div className="cmbr-grid">
              {shownOffers.map((c) => (
                <div key={c.id} className="cmbr-card">
                  <div className="cmbr-card__top">
                    <div>
                      <h3>{c.name}</h3>
                      {c.description ? <p className="cmb-hint">{c.description}</p> : null}
                    </div>
                    <div className="cmbr-price">{formatRupees(c.price)}</div>
                  </div>

                  <ul className="cmbr-list">
                    {c.items.map((i) => (
                      <li key={`${i.provider}-${i.plan_id}`} className={`is-${i.status}`}>
                        <span className="cmbr-list__name">{comboLabel(i.provider)}</span>
                        <span className="cmbr-list__plan">{i.plan_name || "—"}</span>
                        {i.is_free ? <em className="cmbr-free">FREE</em> : null}
                        {i.status === "skipped" ? <span className="cmb-badge cmb-badge--skipped">Will be skipped</span> : null}
                      </li>
                    ))}
                  </ul>

                  {c.iptv_months ? (
                    <div className="cmb-hint">
                      IPTV {c.iptv_months} {c.iptv_months === 1 ? "month" : "months"} · FTA included · add-ons optional
                    </div>
                  ) : null}

                  {c.blockers.length > 0 && (
                    <div className="cmb-note cmb-note--warn">
                      {c.blockers.map((b) => <div key={b}>{b}</div>)}
                    </div>
                  )}
                  {c.notes.length > 0 && (
                    <div className="cmb-note">{c.notes.map((n) => <div key={n}>{n}</div>)}</div>
                  )}

                  {(() => {
                    const mine = locks.filter((l) => Number(l.combo_id) === Number(c.id));
                    if (mine.length) {
                      return (
                        <>
                          <div className="cmb-note cmb-note--info">On this combo till {mine[0].till_text}.</div>
                          <button type="button" className="cmb-btn cmb-btn--primary cmbr-card__cta"
                            disabled={!c.can_recharge} onClick={() => openDialog(c, true)}>
                            Advance renewal
                          </button>
                        </>
                      );
                    }
                    return (
                      <button type="button" className="cmb-btn cmb-btn--primary cmbr-card__cta"
                        disabled={!c.can_recharge} onClick={() => openDialog(c)}>
                        Recharge combo
                      </button>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}

          <h2 className="cmbr-title cmbr-title--history">COMBO HISTORY</h2>
          {history.length === 0 ? (
            <div className="cmb-empty">No combo recharges for this customer yet.</div>
          ) : (
            <div className="cmbr-history">
              {history.map((h) => (
                <div key={h.id} className="cmb-panel cmbr-rec">
                  <div className="cmbr-rec__head">
                    <div>
                      <strong>{h.combo_name}</strong>
                      <div className="cmb-hint">{fmtDayTime(h.created_at)} · {formatRupees(h.amount)}</div>
                    </div>
                    <span className={`cmb-badge cmb-badge--${h.status}`}>{STATUS_TEXT[h.status] || h.status}</span>
                  </div>
                  <div className="cmbr-items">
                    {h.items.map((it) => (
                      <ItemRow key={it.id} item={it} busy={busyItem === it.id} onRecheck={onRecheck} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Recharge dialog ─────────────────────────────── */}
      {dlg && (
        <div className="cmb-overlay" role="dialog" aria-modal="true" aria-label="Recharge combo">
          <div className="cmb-modal cmb-modal--narrow">
            <div className="cmb-modal__head">
              <h3>{dlg.result ? "Combo result" : `${dlg.advance ? "Advance renewal" : "Recharge"} — ${dlg.combo.name}`}</h3>
              <button type="button" className="cmb-modal__close" onClick={closeDialog} aria-label="Close">×</button>
            </div>

            {dlg.result ? (
              <>
                <div className="cmb-modal__body">
                  <div className={`cmb-note ${dlg.result.recharge.status === "success" ? "cmb-note--info" : "cmb-note--warn"}`}>
                    {dlg.result.recharge.status === "success"
                      ? `All plans were recharged. ${formatRupees(dlg.result.recharge.amount)} was charged.`
                      : `${formatRupees(dlg.result.recharge.amount)} was charged once. Some plans did not complete — use Recheck on them below. You will not be charged again.`}
                  </div>
                  {dlg.result.message ? <div className="cmb-note cmb-note--warn">{dlg.result.message}</div> : null}
                  <div className="cmbr-items">
                    {dlg.result.recharge.items.map((it) => (
                      <ItemRow key={it.id} item={it} busy={busyItem === it.id} onRecheck={onRecheck} />
                    ))}
                  </div>
                </div>
                <div className="cmb-modal__foot">
                  <button type="button" className="cmb-btn cmb-btn--primary" onClick={closeDialog}>Done</button>
                </div>
              </>
            ) : (
              <>
                <div className="cmb-modal__body">
                  <div className="cmbr-summary">
                    <div className="cmb-chips">
                      {dlg.combo.items.map((i) => (
                        <span key={`${i.provider}-${i.plan_id}`} className={`cmb-chip ${i.is_free ? "cmb-chip--free" : ""}`}>
                          <strong>{comboLabel(i.provider)}</strong><span>{i.plan_name}</span>{i.is_free ? <em>FREE</em> : null}
                        </span>
                      ))}
                    </div>
                    <div className="cmbr-summary__price">
                      {formatRupees(payTotal)}
                      {addonTotal > 0 ? (
                        <div className="cmb-hint">
                          Combo {formatRupees(dlg.combo.price)} + add-ons {formatRupees(addonMonthly)}{iptvMonths > 1 ? ` × ${iptvMonths}` : ""}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {renderInputs()}

                  {walletShort && (
                    <div className="cmb-note cmb-note--danger">
                      Wallet balance {formatRupees(wallet)} is less than {formatRupees(payTotal)}.
                    </div>
                  )}
                  {allMissing.length > 0 && (
                    <div className="cmb-note cmb-note--warn">{allMissing.map((m) => <div key={m}>{m}</div>)}</div>
                  )}
                  <div className="cmb-hint">
                    {formatRupees(payTotal)} is taken from the wallet once, now. Each provider is then recharged
                    one after another; this can take a little while — keep this window open.
                  </div>
                </div>
                <div className="cmb-modal__foot">
                  <button type="button" className="cmb-btn" onClick={closeDialog} disabled={submitting}>Cancel</button>
                  <button type="button" className="cmb-btn cmb-btn--primary" onClick={submit}
                    disabled={submitting || allMissing.length > 0 || walletShort}>
                    {submitting ? "Recharging…" : `Pay ${formatRupees(payTotal)} & recharge`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm a resend when the provider can't tell us ── */}
      {confirmItem && (
        <div className="cmb-overlay cmbr-overlay-top" role="alertdialog" aria-modal="true">
          <div className="cmb-modal cmb-modal--narrow">
            <div className="cmb-modal__head">
              <h3>Confirm {comboLabel(confirmItem.item.provider)} recharge</h3>
              <button type="button" className="cmb-modal__close" onClick={() => setConfirmItem(null)} aria-label="Close">×</button>
            </div>
            <div className="cmb-modal__body">
              <div className="cmb-note cmb-note--warn">{confirmItem.message}</div>
              <div className="cmb-hint">No extra amount is charged to the wallet either way.</div>
            </div>
            <div className="cmb-modal__foot">
              <button type="button" className="cmb-btn" onClick={() => setConfirmItem(null)}>Not now</button>
              <button type="button" className="cmb-btn cmb-btn--primary"
                onClick={() => { const it = confirmItem.item; setConfirmItem(null); doRecheck(it, true); }}>
                It did not go through — send again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboRechargeTab;
